import { Component, ViewChild, Output, EventEmitter, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxBoxModule,
  DxButtonModule,
  DxDateBoxModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
  DxToastModule,
  DxValidatorModule,
  DxValidationGroupModule,
  DxValidationGroupComponent,
} from 'devextreme-angular';
import { ConfigService } from 'src/app/services/config.service';
import { ContactService } from 'src/app/services/contact.service';
import { GridConnectionService } from 'src/app/services/grid-connection.service';
import { AppGlobals } from '../../app.globals';
import { Router } from '@angular/router';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { GpdPillComponent } from '../gpd-pill/gpd-pill.component';
import { FormFooterComponent } from '../form-footer/form-footer.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ValidationToastComponent } from '../validation-toast/validation-toast.component';
import { Observable, of } from 'rxjs';
import { HasChanges } from 'src/app/services/guards/unsaved-changes.guard';

import { SearchableDropdownDirective } from '../../directives/searchable-dropdown.directive';
@Component({
    selector: 'app-contacts-add-person',
    imports: [
        CommonModule,
        DxButtonModule,
        DxTagBoxModule,
        DxToastModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        DxDateBoxModule,
        DxSelectBoxModule,
        GpdButtonComponent,
        GpdPillComponent,
        FormFooterComponent,
        SvgIconComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: './contacts-add-person.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./contacts-add-person.component.css']
})
export class ContactsAddPersonComponent implements OnInit, HasChanges {
  type: any = '';
  message: any = '';
  toastVisible: boolean = false;

  @ViewChild('validationToast') validationToast: ValidationToastComponent;

  // Same unsaved-changes guard as the contract form (see hasChangesGuard): baseline
  // snapshot taken once ngOnInit's prefill is done, compared against on every navigation
  // attempt away from this page.
  hasChanges: boolean = false;
  isSavingChanges: boolean = false;
  private initialFormData: any;

  @ViewChild('personValidationGroup') personValidationGroup: DxValidationGroupComponent;

  formData = {
    id: null,
    title: null,
    salutation: null,
    firstname: null,
    lastname: null,
    address: null,
    city: null,
    zipcode: null,
    country: null,
    countryId: null,
    stateId: null,
    phonePrivate: null,
    phoneCompany: null,
    phoneMobile: null,
    fax: null,
    email: null,
    birthDate: null,
    note: null,
    tags: null,
  };

  @Input('returnRouteSubmit') returnRouteSubmit: string;
  @Input('returnRouteAbort') returnRouteAbort: string;

  countries: string[] = [$localize`:@@germany:Germany`, $localize`:@@belgium:Belgium`, $localize`:@@france:France`];

  contactTags: any;

  personData: any;

  salutations = [{ name: 'Herr' }, { name: 'Frau' }];

  @Output() onSubmit = new EventEmitter<any>();
  @Output() onAbort = new EventEmitter<any>();

  countriesByLanguageList: any = [];
  statesByLanguageList: any = [];

  constructor(
    private configService: ConfigService,
    private contactService: ContactService,
    private gridConnectionService: GridConnectionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Snapshot taken synchronously, before any await: loadContactTags() below is a real
    // network call the very first time it's called in a session (uncached) - awaiting it
    // here delayed the baseline snapshot long enough that a fast back-button press right
    // after landing on the page raced ahead of it, leaving initialFormData unset and the
    // unsaved-changes guard silently reporting no changes.
    this.initialFormData = JSON.parse(JSON.stringify(this.formData));

    this.loadAsyncData();
  }

  private async loadAsyncData(): Promise<void> {
    this.contactTags = await this.configService.loadContactTags();
    this.getCountriesByLanguage();
    /*     this.getStatesByLanguage(2); */
  }

  checkHasChanges(): void {
    if (!this.initialFormData) {
      this.hasChanges = false;
      return;
    }
    this.hasChanges = JSON.stringify(this.formData) !== JSON.stringify(this.initialFormData);
  }

  saveUnsavedChanges(): Observable<boolean> {
    this.handleSubmitButton(null);
    return of(false);
  }

  // Tag id -> title, for rendering the selected-tags pills above the dropdown.
  getTag(id: any): string {
    return this.contactTags?.find((tag: any) => tag.id == id)?.title ?? '';
  }

  // The "x" on a pill removes just that tag — the dropdown below stays in sync since
  // both render from the same formData.tags array.
  removeTag(id: any): void {
    this.formData.tags = (this.formData.tags ?? []).filter((t: any) => t != id);
  }

  handleSubmitButton(event: any): void {
    const result = this.personValidationGroup.instance.validate();
    if (result.isValid) {
      this.handleSubmit(event);
    } else {
      this.validationToast.showToast($localize`Please fill in all fields before saving`);
    }
  }

  handleAbort(): void {
    this.onAbort.emit({ returnTo: this.returnRouteAbort });
  }

  handleSubmit(e): void {
    //ß e.preventDefault();

    this.hasChanges = false;
    this.isSavingChanges = true;

    this.contactService.addPerson({ PersonItems: [this.formData] }).then(
      result => {
        if (result.hasError === true) {
          this.isSavingChanges = false;
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.onSubmit.emit({ data: result.personItems[0], returnTo: this.returnRouteSubmit });
          this.saveToast({ type: true, feedback: $localize`:@@saved:Saved` });
        }
      },
      (error: any) => {
        this.isSavingChanges = false;
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  saveToast(e: any): void {
    const type = e.type ? 'success' : 'error';
    if ((this.type = 'success')) {
      this.message = e.feedback;
    } else {
      AppGlobals.ShowErrorMessage($localize`:@@errorTryAgain:ERROR: There was an error, please try again.`);
    }
    this.type = type;
    this.toastVisible = true;
  }

  getCountriesByLanguage(): void {
    this.gridConnectionService.getCountries({}).then(
      result => {
        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.countriesByLanguageList = result.countryList;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  /*   getStatesByLanguage(countryID: any): void {
      let selectedCountry = { "CountryId": countryID, }
  
      this.gridConnectionService.getStates(selectedCountry).then((result) => {
        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.statesByLanguageList = result.stateList;
        }
      },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        });
  
    } */

  handleCountryChange(e: any): void {}

  goTo(link) {
    this.router.navigate([link]);
  }
}
